import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ShieldCheck, Activity, PieChart, ArrowUpRight, ArrowRight } from 'lucide-react';

export const InsurerSection = () => {
  return (
    <section className="py-24 md:py-32 relative overflow-hidden bg-[#020408]">
      <div className="w-full max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Visual: The Risk Curve */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="order-2 lg:order-1 relative h-[500px] bg-[#0F141F] rounded-3xl border border-white/10 overflow-hidden shadow-2xl p-8 flex flex-col"
          >
              <div className="flex justify-between items-center mb-8">
                  <div>
                      <h3 className="text-white font-bold text-lg">Portfolio Risk</h3>
                      <p className="text-slate-500 text-xs uppercase tracking-wider">Live Loss Ratio Analysis</p>
                  </div>
                  <div className="flex gap-2">
                      <div className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-xs font-bold flex items-center gap-1">
                          <TrendingUp size={12} /> -12% vs Baseline
                      </div>
                  </div>
              </div>

              {/* Chart Area */}
              <div className="flex-1 relative flex items-end">
                  {/* Grid */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                      {[0, 1, 2, 3, 4].map(i => (
                          <div key={i} className="w-full h-px bg-white/5 border-dashed border-b border-white/5" />
                      ))}
                  </div>

                  {/* Industry Line (Static/Background) */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <path 
                          d="M 0 50 Q 25 45 50 60 T 100 55"
                          fill="none"
                          stroke="#475569"
                          strokeWidth="2"
                          strokeDasharray="4 4"
                      />
                  </svg>

                  {/* VH Performance Line (Animated) */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                      <motion.path 
                          // Starts high risk, goes low risk
                          d="M 0 30 Q 40 30 100 80"
                          fill="none"
                          stroke="#10b981"
                          strokeWidth="3"
                          initial={{ pathLength: 0 }}
                          whileInView={{ pathLength: 1 }}
                          transition={{ duration: 2, ease: "easeInOut" }}
                      />
                      <defs>
                          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="rgba(16, 185, 129, 0.2)" />
                              <stop offset="100%" stopColor="rgba(16, 185, 129, 0)" />
                          </linearGradient>
                      </defs>
                      <motion.path 
                          d="M 0 30 Q 40 30 100 80 L 100 100 L 0 100 Z"
                          fill="url(#areaGradient)"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: 1, duration: 1 }}
                      />
                  </svg>

                  {/* Floating Datapoints */}
                  <motion.div 
                      className="absolute top-[30%] left-[40%] bg-slate-800 text-xs text-white px-2 py-1 rounded shadow-lg border border-white/10"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.5 }}
                  >
                      Intervention
                  </motion.div>
              </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <div className="flex items-center gap-2 text-emerald-500 font-mono text-xs uppercase tracking-widest mb-4">
               <ShieldCheck size={14} />
               <span>For Insurers</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              The Risk Terminal. <br />
              <span className="text-slate-400">Underwrite with truth.</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8 font-light">
              Move beyond annual audits and lagging indicators. The Risk Terminal gives you a live, cryptographic feed of safety performance across your entire portfolio.
            </p>

            <div className="space-y-6 mb-8">
              {[
                { 
                    title: 'Live Loss Ratios', 
                    desc: 'See risk exposure adjust in real-time as sites improve or degrade compliance.' 
                },
                { 
                    title: 'Verification Gates', 
                    desc: 'Automatically verify that every worker on a covered site has valid certifications.' 
                },
                { 
                    title: 'Dynamic Premium Models', 
                    desc: 'Adjust rates monthly based on verified performance, not just historical claims.' 
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                   <div className="mt-1 flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 text-emerald-400">
                      <Activity size={16} />
                   </div>
                   <div>
                      <h4 className="text-white font-bold text-sm mb-1">{item.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                   </div>
                </div>
              ))}
            </div>

            <button className="flex items-center gap-2 text-emerald-400 font-bold hover:gap-3 transition-all group">
                Access Risk Terminal <ArrowRight size={18} />
            </button>
          </motion.div>

        </div>
      </div>
    </section>
  );
};
